const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const ordered = (value: unknown): unknown => {
    if(Array.isArray(value)){ return value.map(ordered); }
    if(isObject(value)){ return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => [key, ordered(entry)])); }
    return value;
};
const signature = (value: unknown): string => JSON.stringify(ordered(value)) ?? '';
const asSet = (values: unknown[]): unknown[] => [...new Map(values.map(value => [signature(value), value])).entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, value]) => value);

const filterValue = (value: unknown): unknown => {
    if(typeof value === 'string' && value.trim().startsWith('[')){
        try { const parsed: unknown = JSON.parse(value); if(Array.isArray(parsed)){ return filterValue(parsed); } } catch { /* A text filter may contain brackets. */ }
    }
    if(Array.isArray(value)){ return value.length ? asSet(value) : undefined; }
    // false and 0 are real filters (for example, payment not blocked).
    return value === '' || value === 'all' || value == null ? undefined : value;
};

const normalize = (value: unknown): unknown => {
    if(!isObject(value)){ return value; }
    const result = { ...value };
    if(typeof result.search === 'string'){ result.search = result.search.trim(); }
    if(isObject(result.filters)){
        result.filters = Object.fromEntries(Object.entries(result.filters).map(([key, entry]) => [key,
            key.startsWith('column:') && typeof entry === 'string' ? (entry.trim() || undefined) : filterValue(entry),
        ]).filter(([, entry]) => entry !== undefined));
    }
    if(Array.isArray(result.columnFilters)){
        result.columnFilters = asSet(result.columnFilters.filter(isObject).map(entry => ({ ...entry, value: filterValue(entry.value) })).filter(entry => entry.value !== undefined));
    }
    if(isObject(result.preferences) && Array.isArray(result.preferences.hidden)){
        result.preferences = { ...result.preferences, hidden: asSet(result.preferences.hidden) };
    }
    return result;
};

export const viewSignature = (value: unknown): string => signature(normalize(value));
export type ViewChange = 'search' | 'filters' | 'columns' | 'sort' | 'density' | 'page_size' | 'status' | 'settings';
const categories: Record<string, ViewChange> = { search: 'search', filters: 'filters', columnFilters: 'filters', client: 'filters', year: 'filters', due: 'filters', sort: 'sort', sorting: 'sort', pageSize: 'page_size', tab: 'status', context: 'status' };

/** Compare effective settings; never rewrite the user's stored view. */
export const viewChanges = (value: unknown, baseline: unknown): ViewChange[] => {
    const current = normalize(value);
    const previous = normalize(baseline);
    if(!isObject(current) || !isObject(previous)){ return signature(current) === signature(previous) ? [] : ['settings']; }
    const changes = new Set<ViewChange>();
    for(const key of new Set([...Object.keys(current), ...Object.keys(previous)])){
        if(signature(current[key]) === signature(previous[key])){ continue; }
        if(key === 'preferences' && isObject(current[key]) && isObject(previous[key])){
            const next = current[key]; const before = previous[key];
            if(signature(next.hidden) !== signature(before.hidden)){ changes.add('columns'); }
            if(next.density !== before.density){ changes.add('density'); }
        }
        else{ changes.add(categories[key] ?? 'settings'); }
    }
    return [...changes];
};
